/**
 * Timeline slice - manages timeline items
 */

/* eslint-disable no-param-reassign, no-plusplus, no-shadow */
import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  items: [],
  selectedItemId: null,
  lastAddedItemId: null,
};

const timelineSlice = createSlice({
  name: 'timeline',
  initialState,
  reducers: {
    addTimelineItem: (state, action) => {
      const { assetId, duration, currentTime = 0, id, trimStart, trimEnd, metadata, anchorItemId, shouldAutoSeek = true, shouldSelectItem = true } =
        action.payload;

      const newItem = {
        id: id || `timeline-item-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        asset_id: assetId,
        order: 0,
        start_time: 0,
        duration,
        settings: {
          volume: 1,
          trim_start: trimStart !== undefined ? trimStart : 0,
          trim_end: trimEnd !== undefined ? trimEnd : 0,
        },
        metadata: metadata || {},
      };

      let insertIndex = state.items.length;

      // Handle anchorItemId: 
      // - explicitly null -> insert at beginning (index 0) - used for undo of first item
      // - explicitly provided (string) -> insert after that item
      // - undefined -> fallback to selected item, else time-based
      if (anchorItemId === null) {
        // Explicitly null means insert at the beginning
        insertIndex = 0;
      } else if (anchorItemId !== undefined) {
        // Explicitly provided anchor item ID
        const selIndex = state.items.findIndex((i) => i.id === anchorItemId);
        insertIndex = selIndex >= 0 ? selIndex + 1 : state.items.length;
      } else {
        // anchorItemId is undefined, use fallback logic
        const anchorId = state.selectedItemId;
        if (anchorId) {
          const selIndex = state.items.findIndex((i) => i.id === anchorId);
          insertIndex = selIndex >= 0 ? selIndex + 1 : state.items.length;
        } else if (currentTime > 0 && state.items.length > 0) {
        for (let i = 0; i < state.items.length; i++) {
          const item = state.items[i];
          const itemEnd = item.start_time + item.duration;

          if (currentTime >= item.start_time && currentTime < itemEnd) {
            insertIndex = i + 1;
            break;
          } else if (
            currentTime >= itemEnd &&
            i === state.items.length - 1
          ) {
            insertIndex = state.items.length;
          }
        }
      }
      }

      state.items.splice(insertIndex, 0, newItem);

      state.items.forEach((timelineItem, index) => {
        timelineItem.order = index;
        timelineItem.start_time = state.items
          .slice(0, index)
          .reduce((acc, i) => acc + i.duration, 0);
      });

      // Only set selectedItemId if shouldSelectItem is true (default true, false during batch loads)
      // This prevents the jumping effect when loading multiple items at once
      if (shouldSelectItem) {
        state.selectedItemId = newItem.id;
      }
      // Only set lastAddedItemId if shouldAutoSeek is true (default true, false during batch loads)
      if (shouldAutoSeek) {
        state.lastAddedItemId = newItem.id;
      }
    },
    removeTimelineItem: (state, action) => {
      const itemId = action.payload;
      state.items = state.items.filter((item) => item.id !== itemId);

      state.items.forEach((timelineItem, index) => {
        timelineItem.order = index;
        timelineItem.start_time = state.items
          .slice(0, index)
          .reduce((acc, i) => acc + i.duration, 0);
      });

      if (state.selectedItemId === itemId) {
        state.selectedItemId = state.items.length > 0 ? state.items[0].id : null;
      }
    },
    selectItem: (state, action) => {
      state.selectedItemId = action.payload;
    },
    reorderItems: (state, action) => {
      // Create new array with updated order and start_time for each item
      state.items = action.payload.map((timelineItem, index) => ({
        ...timelineItem,
        order: index,
        start_time: action.payload
          .slice(0, index)
          .reduce((acc, i) => acc + i.duration, 0),
      }));
    },
    clearTimeline: (state) => {
      state.items = [];
      state.selectedItemId = null;
      state.lastAddedItemId = null;
    },
    updateItemTrim: (state, action) => {
      const { itemId, trimStart, trimEnd, originalDuration } = action.payload;
      const item = state.items.find((i) => i.id === itemId);
      if (!item) return;

      const validTrimStart = Math.max(
        0,
        Math.min(trimStart, originalDuration - 0.5)
      );
      const validTrimEnd = Math.max(
        0,
        Math.min(trimEnd, originalDuration - validTrimStart - 0.5)
      );

      item.settings.trim_start = validTrimStart;
      item.settings.trim_end = validTrimEnd;
      item.duration = originalDuration - validTrimStart - validTrimEnd;

      state.items.forEach((timelineItem, index) => {
        timelineItem.order = index;
        timelineItem.start_time = state.items
          .slice(0, index)
          .reduce((acc, i) => acc + i.duration, 0);
      });
    },
    updateItemMetadata: (state, action) => {
      const { itemId, metadata } = action.payload;
      const item = state.items.find((i) => i.id === itemId);
      if (!item) return;
      
      item.metadata = {
        ...item.metadata,
        ...metadata,
      };
    },
  },
});

export const {
  addTimelineItem,
  removeTimelineItem,
  selectItem,
  reorderItems,
  clearTimeline,
  updateItemTrim,
  updateItemMetadata,
} = timelineSlice.actions;

export const selectTotalDuration = (state) => {
  return state.timeline.items.reduce((acc, item) => acc + item.duration, 0);
};

export const selectSelectedItem = (state) => {
  return (
    state.timeline.items.find(
      (item) => item.id === state.timeline.selectedItemId
    ) || null
  );
};

export default timelineSlice.reducer;

